// src/components/visualizations/GuidedVisualization.tsx
// Guided meditation/visualization experiences

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft, Play, Pause, RotateCcw, Volume2, VolumeX,
  Sparkles, Moon, Sun, Heart, Star, Clock, Eye, ChevronRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase/browserClient';
import { useAuth } from '../../hooks/useAuth';
import { useSoulFile } from '../../hooks/useSoulFile';
import { ToolHeader } from '../ui/ToolHeader';

interface Visualization {
  id: string;
  title: string;
  description: string;
  duration: number; // in minutes
  category: 'manifestation' | 'relaxation' | 'confidence' | 'abundance' | 'healing';
  icon: React.ElementType;
  color: string;
  steps: VisualizationStep[];
}

interface VisualizationStep {
  text: string;
  duration: number; // in seconds
  breathe?: 'in' | 'out' | 'hold';
}

const VISUALIZATIONS: Visualization[] = [
  {
    id: 'future-self',
    title: 'Meet Your Future Self',
    description: 'Connect with the version of you who has already achieved your dreams',
    duration: 10,
    category: 'manifestation',
    icon: Star,
    color: 'from-purple-500 to-indigo-500',
    steps: [
      { text: "Find a comfortable position and close your eyes. Take a deep breath in...", duration: 8, breathe: 'in' },
      { text: "And slowly release...", duration: 6, breathe: 'out' },
      { text: "Continue breathing deeply, letting go of any tension in your body.", duration: 15 },
      { text: "Imagine yourself standing at the beginning of a beautiful path. The air is warm and gentle.", duration: 12 },
      { text: "As you walk forward, you notice the path leads to a door made of golden light.", duration: 12 },
      { text: "This door leads to a moment in your future - a moment where you've become who you're meant to be.", duration: 15 },
      { text: "Take a deep breath, and step through the door...", duration: 8, breathe: 'in' },
      { text: "On the other side, you see yourself - your future self. Notice how they stand, how they hold themselves.", duration: 15 },
      { text: "Look into their eyes. What do you see there? Peace? Confidence? Joy?", duration: 15 },
      { text: "Your future self smiles at you. They have a message for you about your journey.", duration: 12 },
      { text: "Listen carefully... What wisdom do they share?", duration: 20 },
      { text: "They reach out and place their hand on your heart. You feel a warm energy transfer between you.", duration: 15 },
      { text: "This energy is the knowing that you are already becoming this person.", duration: 12 },
      { text: "Thank your future self. Know that you can return here anytime.", duration: 10 },
      { text: "Slowly begin to return, bringing this energy with you. Take a deep breath in...", duration: 8, breathe: 'in' },
      { text: "And gently release, feeling refreshed and aligned.", duration: 8, breathe: 'out' },
      { text: "When you're ready, slowly open your eyes.", duration: 10 },
    ]
  },
  {
    id: 'abundance-flow',
    title: 'Abundance Flow',
    description: 'Open yourself to receive prosperity and abundance in all forms',
    duration: 8,
    category: 'abundance',
    icon: Sparkles,
    color: 'from-amber-500 to-orange-500',
    steps: [
      { text: "Close your eyes and take a deep, cleansing breath...", duration: 8, breathe: 'in' },
      { text: "Release any worries about money or scarcity...", duration: 8, breathe: 'out' },
      { text: "Imagine yourself surrounded by a warm, golden light.", duration: 12 },
      { text: "This light is the energy of abundance - infinite and ever-present.", duration: 12 },
      { text: "Feel this golden energy beginning to flow into the top of your head.", duration: 10 },
      { text: "It moves through your mind, clearing any beliefs of lack or limitation.", duration: 12 },
      { text: "The golden light flows down into your heart, expanding your capacity to receive.", duration: 12 },
      { text: "Feel your heart opening... You are worthy of abundance.", duration: 10 },
      { text: "The energy continues flowing through your body, filling every cell.", duration: 12 },
      { text: "Imagine streams of abundance flowing toward you from all directions.", duration: 12 },
      { text: "Money, opportunities, love, joy - all flowing easily to you.", duration: 12 },
      { text: "You are a magnet for good things. You receive with gratitude.", duration: 12 },
      { text: "Take a moment to feel truly abundant, right now, exactly as you are.", duration: 15 },
      { text: "Carry this feeling with you. The universe is always providing.", duration: 10 },
      { text: "Take a deep breath, anchoring this abundance within you.", duration: 8, breathe: 'in' },
      { text: "When you're ready, gently open your eyes.", duration: 8 },
    ]
  },
  {
    id: 'inner-peace',
    title: 'Inner Peace',
    description: 'Release stress and find calm in the present moment',
    duration: 7,
    category: 'relaxation',
    icon: Moon,
    color: 'from-blue-500 to-cyan-500',
    steps: [
      { text: "Let your eyes gently close. You are safe here.", duration: 8 },
      { text: "Take a slow, deep breath in through your nose...", duration: 6, breathe: 'in' },
      { text: "Hold for a moment...", duration: 4, breathe: 'hold' },
      { text: "And slowly exhale through your mouth...", duration: 8, breathe: 'out' },
      { text: "With each breath out, feel your body becoming heavier, more relaxed.", duration: 12 },
      { text: "Imagine you're lying on a soft cloud, floating gently in a peaceful sky.", duration: 12 },
      { text: "There is nothing you need to do. Nowhere you need to be.", duration: 10 },
      { text: "Let go of any thoughts. Watch them drift away like clouds.", duration: 12 },
      { text: "Feel the peace that exists beneath all the noise of daily life.", duration: 12 },
      { text: "This peace is always within you. It is your true nature.", duration: 10 },
      { text: "Breathe in peace...", duration: 6, breathe: 'in' },
      { text: "Breathe out tension...", duration: 6, breathe: 'out' },
      { text: "Breathe in calm...", duration: 6, breathe: 'in' },
      { text: "Breathe out worry...", duration: 6, breathe: 'out' },
      { text: "Rest here in this stillness for a moment.", duration: 15 },
      { text: "Know that you can return to this peace anytime with a single breath.", duration: 10 },
      { text: "Gently begin to return. Wiggle your fingers and toes.", duration: 8 },
      { text: "When ready, slowly open your eyes, carrying this peace with you.", duration: 10 },
    ]
  },
  {
    id: 'confidence-boost',
    title: 'Unshakeable Confidence',
    description: 'Reconnect with your inner power and self-belief',
    duration: 8,
    category: 'confidence',
    icon: Sun,
    color: 'from-rose-500 to-pink-500',
    steps: [
      { text: "Stand or sit tall. Close your eyes and take a powerful breath in...", duration: 8, breathe: 'in' },
      { text: "Exhale fully, releasing any self-doubt...", duration: 8, breathe: 'out' },
      { text: "Feel your feet firmly connected to the ground. You are rooted. Stable.", duration: 12 },
      { text: "Imagine a brilliant light forming in your solar plexus - your power center.", duration: 12 },
      { text: "This light is your confidence. Your inner fire. Your truth.", duration: 10 },
      { text: "Feel it growing brighter with each breath.", duration: 10 },
      { text: "The light expands through your chest, filling you with courage.", duration: 12 },
      { text: "It rises to your throat, empowering your voice to speak your truth.", duration: 12 },
      { text: "It illuminates your mind, clearing away fear and doubt.", duration: 10 },
      { text: "Remember a moment when you felt truly confident. Let that feeling fill you now.", duration: 15 },
      { text: "You have overcome challenges before. You will overcome them again.", duration: 12 },
      { text: "Say silently to yourself: 'I am capable. I am worthy. I am enough.'", duration: 12 },
      { text: "Feel these words as truth, not just thoughts.", duration: 10 },
      { text: "The light within you now radiates outward, surrounding you in an aura of confidence.", duration: 12 },
      { text: "Others can feel this energy. It draws opportunities to you.", duration: 10 },
      { text: "Take a deep breath, sealing this confidence within you.", duration: 8, breathe: 'in' },
      { text: "Open your eyes, ready to move through your day with power.", duration: 8 },
    ]
  },
  {
    id: 'heart-healing',
    title: 'Heart Healing',
    description: 'Release emotional pain and open your heart to love',
    duration: 10,
    category: 'healing',
    icon: Heart,
    color: 'from-pink-500 to-rose-400',
    steps: [
      { text: "Find a comfortable position. Place your hand gently over your heart.", duration: 10 },
      { text: "Take a soft breath in, sending love to your heart space...", duration: 8, breathe: 'in' },
      { text: "And release, letting your heart soften...", duration: 8, breathe: 'out' },
      { text: "Your heart has carried so much. It's time to let it rest.", duration: 12 },
      { text: "Imagine a soft, pink light beginning to glow beneath your hand.", duration: 12 },
      { text: "This light is pure love - unconditional and healing.", duration: 10 },
      { text: "Let it gently touch any places in your heart that feel wounded or closed.", duration: 15 },
      { text: "There's no need to relive old pain. Simply acknowledge it exists, and let the light touch it.", duration: 15 },
      { text: "With each breath, the light grows stronger, dissolving old hurt.", duration: 12 },
      { text: "Forgiveness flows through you - for others, and most importantly, for yourself.", duration: 12 },
      { text: "You did the best you could with what you knew. So did everyone else.", duration: 12 },
      { text: "Feel your heart beginning to open, like a flower blooming.", duration: 12 },
      { text: "There is space here now. Space for new love, new joy, new connections.", duration: 12 },
      { text: "Say gently to yourself: 'I am worthy of love. My heart is safe to open.'", duration: 12 },
      { text: "Feel the truth of these words.", duration: 10 },
      { text: "Take a deep breath, filling your healed heart with fresh energy.", duration: 8, breathe: 'in' },
      { text: "Exhale gratitude for your heart's resilience.", duration: 8, breathe: 'out' },
      { text: "Keep your hand on your heart a moment longer, then gently release.", duration: 10 },
      { text: "When you're ready, open your eyes with a soft, open heart.", duration: 10 },
    ]
  },
];

interface GuidedVisualizationProps {
  onClose?: () => void;
  embedded?: boolean;
}

export function GuidedVisualization({ onClose, embedded = false }: GuidedVisualizationProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { soulFile } = useSoulFile();
  
  const [selectedViz, setSelectedViz] = useState<Visualization | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [stepProgress, setStepProgress] = useState(0);
  const [completed, setCompleted] = useState(false);
  
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const progressRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (progressRef.current) clearInterval(progressRef.current);
    };
  }, []);

  // Handle step progression
  useEffect(() => {
    if (!isPlaying || !selectedViz) return;

    const currentStep = selectedViz.steps[currentStepIndex];
    if (!currentStep) {
      // Visualization complete
      setIsPlaying(false);
      setCompleted(true);
      logCompletion();
      return;
    }

    // Progress bar animation
    setStepProgress(0);
    progressRef.current = setInterval(() => {
      setStepProgress(prev => {
        const increment = 100 / (currentStep.duration * 10);
        return Math.min(prev + increment, 100);
      });
    }, 100);

    // Move to next step after duration
    timerRef.current = setTimeout(() => {
      setCurrentStepIndex(prev => prev + 1);
    }, currentStep.duration * 1000);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (progressRef.current) clearInterval(progressRef.current);
    };
  }, [isPlaying, currentStepIndex, selectedViz]);

  const logCompletion = async () => {
    if (!user?.id || !selectedViz) return;
    
    await supabase.from('activity_log').insert({
      user_id: user.id,
      activity_type: 'visualization',
      activity_date: new Date().toISOString().split('T')[0],
      metadata: { visualization_id: selectedViz.id }
    });
  };

  const startVisualization = (viz: Visualization) => {
    setSelectedViz(viz);
    setCurrentStepIndex(0);
    setStepProgress(0);
    setCompleted(false);
    setIsPlaying(true);
  };

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const restart = () => {
    setCurrentStepIndex(0);
    setStepProgress(0);
    setCompleted(false);
    setIsPlaying(true);
  };

  const exitVisualization = () => {
    setIsPlaying(false);
    setSelectedViz(null);
    setCurrentStepIndex(0);
    setCompleted(false);
    if (timerRef.current) clearTimeout(timerRef.current);
    if (progressRef.current) clearInterval(progressRef.current);
  };

  const handleClose = () => {
    if (embedded && onClose) {
      onClose();
    } else {
      navigate('/chat');
    }
  };

  // Selection screen uses sanctuary background, active visualization uses immersive dark background
  const wrapperClass = !selectedViz
    ? (embedded ? "flex-1 overflow-y-auto" : "min-h-screen bg-gradient-to-br from-pink-100/40 via-purple-50/40 to-cyan-50/40")
    : (embedded ? "flex-1 overflow-y-auto bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900" : "min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900");

  const currentStep = selectedViz?.steps[currentStepIndex];

  return (
    <div className={wrapperClass}>
      <AnimatePresence mode="wait">
        {!selectedViz ? (
          // Selection Screen
          <motion.div
            key="selection"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <ToolHeader
              title="Guided Visualizations"
              onBack={handleClose}
              backLabel="Chat"
            />

            <div className="max-w-xl mx-auto px-6">
              <div className="text-center mb-8">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-purple-100 to-pink-100 border border-white/60 flex items-center justify-center">
                  <Eye className="w-8 h-8 text-purple-500" />
                </div>
                <h3 className="text-lg font-semibold text-purple-800 mb-2">Guided Visualizations</h3>
                <p className="text-purple-500 text-sm max-w-sm mx-auto">
                  Close your eyes and step into your future self. Choose a visualization to begin.
                </p>
              </div>

              <div className="space-y-4">
                {VISUALIZATIONS.map((viz) => (
                  <motion.button
                    key={viz.id}
                    onClick={() => startVisualization(viz)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full p-5 rounded-xl text-left bg-white/30 backdrop-blur-md border border-white/50 hover:bg-white/40 hover:border-purple-200 hover:shadow-lg transition-all group"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-100 to-pink-100 border border-white/60 flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
                        <viz.icon className="w-6 h-6 text-purple-600" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-purple-800 group-hover:text-purple-900">{viz.title}</h4>
                        <p className="text-sm text-purple-500 mt-1">{viz.description}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <Clock className="w-4 h-4 text-purple-400" />
                          <span className="text-xs text-purple-400">{viz.duration} min</span>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-purple-400 group-hover:text-purple-600 transition-colors" />
                    </div>
                  </motion.button>
                ))}
              </div>
            </div>
          </motion.div>
        ) : (
          // Visualization Player
          <motion.div
            key="player"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="min-h-screen flex flex-col"
          >
            {/* Exit button */}
            <div className="absolute top-4 left-4 z-50">
              <button
                onClick={exitVisualization}
                className="p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-full transition-colors"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
            </div>

            {/* Main content */}
            <div className="flex-1 flex flex-col items-center justify-center px-6 py-20">
              {completed ? (
                // Completion Screen
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center"
                >
                  <div className={`w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br ${selectedViz.color} flex items-center justify-center`}>
                    <Sparkles className="w-12 h-12 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-2">Beautiful ✨</h2>
                  <p className="text-purple-200 mb-8 max-w-md">
                    Take a moment to notice how you feel. Carry this energy with you.
                  </p>
                  <div className="flex gap-3 justify-center">
                    <button
                      onClick={restart}
                      className="px-6 py-3 bg-white/10 text-white rounded-xl hover:bg-white/20 transition-colors flex items-center gap-2"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Again
                    </button>
                    <button
                      onClick={exitVisualization}
                      className="px-6 py-3 bg-white text-purple-900 rounded-xl hover:bg-white/90 transition-colors"
                    >
                      Done
                    </button>
                  </div>
                </motion.div>
              ) : (
                // Playing Screen
                <>
                  {/* Breathing indicator */}
                  {currentStep?.breathe && (
                    <motion.div
                      animate={{
                        scale: currentStep.breathe === 'in' ? [1, 1.3] : 
                               currentStep.breathe === 'out' ? [1.3, 1] : 1,
                      }}
                      transition={{ duration: currentStep.duration, ease: 'easeInOut' }}
                      className={`w-32 h-32 rounded-full bg-gradient-to-br ${selectedViz.color} opacity-30 mb-8`}
                    />
                  )}

                  {/* Step text */}
                  <AnimatePresence mode="wait">
                    <motion.p
                      key={currentStepIndex}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className="text-xl md:text-2xl text-white text-center max-w-lg leading-relaxed mb-8"
                    >
                      {currentStep?.text}
                    </motion.p>
                  </AnimatePresence>

                  {/* Breathing label */}
                  {currentStep?.breathe && (
                    <p className="text-purple-300 text-sm uppercase tracking-widest mb-8">
                      {currentStep.breathe === 'in' ? 'Breathe In' : 
                       currentStep.breathe === 'out' ? 'Breathe Out' : 'Hold'}
                    </p>
                  )}

                  {/* Progress */}
                  <div className="w-full max-w-xs">
                    <div className="h-1 bg-white/20 rounded-full overflow-hidden">
                      <motion.div
                        className={`h-full bg-gradient-to-r ${selectedViz.color}`}
                        style={{ width: `${stepProgress}%` }}
                      />
                    </div>
                    <p className="text-purple-300 text-xs text-center mt-2">
                      {currentStepIndex + 1} / {selectedViz.steps.length}
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* Controls */}
            {!completed && (
              <div className="pb-12 flex justify-center gap-4">
                <button
                  onClick={restart}
                  className="p-4 text-white/60 hover:text-white hover:bg-white/10 rounded-full transition-colors"
                >
                  <RotateCcw className="w-6 h-6" />
                </button>
                <button
                  onClick={togglePlayPause}
                  className={`p-5 rounded-full bg-gradient-to-br ${selectedViz.color} text-white shadow-lg hover:opacity-90 transition-opacity`}
                >
                  {isPlaying ? <Pause className="w-8 h-8" /> : <Play className="w-8 h-8 ml-1" />}
                </button>
                <div className="w-14" /> {/* Spacer for symmetry */}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default GuidedVisualization;
